import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { S } from "../../data/strings";
import { CONTACT } from "../../data/content";
import { WhatsappIcon } from "./Header";

export const Footer = () => {
  const { t, lang } = useLang();

  const quick = [
    { label: S.footer.home, href: "#home" },
    { label: S.footer.about, href: "#about" },
    { label: S.footer.collections, href: "#collections" },
    { label: S.footer.contact, href: "#contact" },
  ];
  const cols = [
    { label: { en: "Ittar & Fragrances", hi: "इत्र व सुगंध" }, href: "#ittar" },
    { label: { en: "Puja Essentials", hi: "पूजा सामग्री" }, href: "#puja" },
    { label: { en: "Thakur Ji Poshak", hi: "ठाकुर जी पोशाक" }, href: "#custom" },
    { label: { en: "Mukut & Shringar", hi: "मुकुट व श्रृंगार" }, href: "#mukut" },
    { label: { en: "Customised Dresses", hi: "कस्टम पोशाक" }, href: "#custom" },
  ];
  const policies = [
    { label: S.footer.privacy, slug: "privacy" },
    { label: S.footer.terms, slug: "terms" },
    { label: S.footer.shipping, slug: "shipping" },
    { label: S.footer.returns, slug: "returns" },
  ];

  return (
    <footer data-testid="footer" className="relative bg-velvet text-ivory">
      {/* gold ornamental line */}
      <div className="flex items-center justify-center gap-4 py-6" aria-hidden="true">
        <span className="h-px w-24 sm:w-40 bg-gradient-to-r from-transparent to-gold/60" />
        <span className="text-gold text-lg">&#10070;</span>
        <span className="h-px w-24 sm:w-40 bg-gradient-to-l from-transparent to-gold/60" />
      </div>

      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14 pt-6">
          {/* Brand */}
          <div className="lg:col-span-1">
            <p className="font-serif text-2xl text-ivory">{t(S.brand)}</p>
            <p className="font-cinzel text-[10px] tracking-luxe uppercase text-gold mt-1">{t(S.brandSub)}</p>
            <p className="mt-5 text-ivory/60 text-sm leading-relaxed max-w-xs">{t(S.footer.desc)}</p>
            <div className="mt-6 flex items-center gap-3">
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="flex items-center justify-center w-10 h-10 rounded-full border border-gold/30 text-gold hover:bg-gold hover:text-burgundy transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="flex items-center justify-center w-10 h-10 rounded-full border border-gold/30 text-gold hover:bg-gold hover:text-burgundy transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" aria-label="WhatsApp" className="flex items-center justify-center w-10 h-10 rounded-full border border-gold/30 text-gold hover:bg-gold hover:text-burgundy transition-colors">
                <WhatsappIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="font-cinzel text-xs tracking-luxe uppercase text-gold">{t(S.footer.quickLinks)}</h3>
            <ul className="mt-5 space-y-3">
              {quick.map((q) => (
                <li key={q.href}><a href={q.href} className="text-ivory/65 text-sm hover:text-goldlight transition-colors">{t(q.label)}</a></li>
              ))}
            </ul>
          </div>

          {/* Collections */}
          <div>
            <h3 className="font-cinzel text-xs tracking-luxe uppercase text-gold">{t(S.footer.collections)}</h3>
            <ul className="mt-5 space-y-3">
              {cols.map((c) => (
                <li key={c.label.en}><a href={c.href} className="text-ivory/65 text-sm hover:text-goldlight transition-colors">{t(c.label)}</a></li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-cinzel text-xs tracking-luxe uppercase text-gold">{t(S.footer.contact)}</h3>
            <ul className="mt-5 space-y-3 text-sm text-ivory/65">
              <li><a href={`tel:${CONTACT.phone}`} className="hover:text-goldlight transition-colors">{CONTACT.phone}</a></li>
              <li><a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" className="hover:text-goldlight transition-colors">WhatsApp: {CONTACT.phone}</a></li>
              <li><a href={`mailto:${CONTACT.email}`} className="hover:text-goldlight transition-colors break-all">{CONTACT.email}</a></li>
              <li className="text-ivory/55 leading-relaxed">{lang === "hi" ? CONTACT.addressHi : CONTACT.addressEn}</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-gold/15 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-ivory/50 text-xs">&copy; 2026 {t(S.brand)}. {t(S.footer.rights)}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {policies.map((p) => (
              <Link key={p.slug} to={`/policy/${p.slug}`} data-testid={`footer-policy-${p.slug}`} className="text-ivory/55 text-xs hover:text-goldlight transition-colors">
                {t(p.label)}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
